import { BaseService } from "./BaseService";
import type {
  GetNotificationsFilters,
  NotificationEntity,
  PaginatedResponse,
  UnreadCountResponse,
  MarkSingleReadDto,
  MarkReadDto,
  MarkBulkResponse,
  UpdateNotificationSettingsDto,
  NotificationSettingsEntity,
  SystemNotificationPolicyMatrixResponse,
  CurrentSystemNotificationPolicyResponse,
  UpdateSystemNotificationPoliciesDto,
  KnownNotificationEvent,
} from "@/types/notifications";

class NotificationsService extends BaseService {
  private basePath = "/notifications";

  /**
   * GET /notifications?...
   */
  async getNotifications(
    filters: GetNotificationsFilters = {},
  ): Promise<PaginatedResponse<NotificationEntity>> {
    return this.get<PaginatedResponse<NotificationEntity>>(
      this.basePath,
      filters,
    );
  }

  /**
   * GET /notifications/unread-count
   */
  async getUnreadCount(
    filters: Omit<GetNotificationsFilters, "isRead" | "page" | "limit"> = {},
  ): Promise<UnreadCountResponse> {
    return this.get<UnreadCountResponse>(
      `${this.basePath}/unread-count`,
      filters,
    );
  }

  /**
   * PATCH /notifications/:id/read
   */
  async markAsRead(
    id: string,
    dto: MarkSingleReadDto,
  ): Promise<NotificationEntity> {
    return this.patch<NotificationEntity>(`${this.basePath}/${id}/read`, dto);
  }

  /**
   * PATCH /notifications/read-bulk
   */
  async markBulkAsRead(dto: MarkReadDto): Promise<MarkBulkResponse> {
    return this.patch<MarkBulkResponse>(`${this.basePath}/read-bulk`, dto);
  }

  /**
   * POST /notifications/read-all
   */
  async markAllAsRead(): Promise<MarkBulkResponse> {
    return this.post<MarkBulkResponse>(`${this.basePath}/read-all`);
  }

  /**
   * GET /notifications/settings
   * @returns user's notification settings
   */
  getSettings(): Promise<NotificationSettingsEntity> {
    return this.get<NotificationSettingsEntity>("/notifications/settings");
  }

  /**
   * PATCH /notifications/settings
   * @param dto fields to update (currently only `isEnabled` is supported)
   * @returns
   */
  updateSettings(
    dto: UpdateNotificationSettingsDto,
  ): Promise<NotificationSettingsEntity> {
    return this.patch<NotificationSettingsEntity>(
      "/notifications/settings",
      dto,
    );
  }

  getSystemSettings(): Promise<SystemNotificationPolicyMatrixResponse> {
    return this.get<SystemNotificationPolicyMatrixResponse>(
      "/notifications/system-settings",
    );
  }

  getCurrentSystemSetting(
    event: KnownNotificationEvent,
  ): Promise<CurrentSystemNotificationPolicyResponse> {
    return this.get<CurrentSystemNotificationPolicyResponse>(
      "/notifications/system-settings/current",
      { event },
    );
  }

  updateSystemSettings(
    dto: UpdateSystemNotificationPoliciesDto,
  ): Promise<SystemNotificationPolicyMatrixResponse> {
    return this.patch<SystemNotificationPolicyMatrixResponse>(
      "/notifications/system-settings",
      dto,
    );
  }
}

export const notificationsService = new NotificationsService();
